import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FocusScope } from '@react-aria/focus';
import { DismissButton } from '@react-aria/overlays';
import cn from 'classnames';
import { useResize } from '@nw/shared/ui';

import {
  DEFAULT_STYLES,
  MOBILE_BOTTOM_SHEET_BREAKPOINT
} from '../../constants';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import {
  readBodyInlineOverflow,
  writeBodyInlineOverflow
} from '../../utils/bodyInlineOverflow';
import {
  getMobileListHeightStyle,
  isMobileViewport,
  isNodeInsideBottomSheet
} from '../../utils/viewportHeightUtils';
import { SelectProps } from '../../types';

import { BottomSheet } from '../BottomSheet';
import { FieldErrorMessage } from '../FieldErrorMessage';
import { Portal } from '../Portal';

export const Select: React.FC<SelectProps> = (props) => {
  const {
    name,
    label,
    value,
    placeholder = 'Select an option',
    options,
    onChange,
    onBlur,
    error,
    helperText,
    isDisabled = false,
    isRequired = false,
    className,
    optionClassName,
    labelClassName,
    placement = 'bottom',
    offset = 4,
    maxHeight = 240,
    closeOnScroll = true,
    shouldCloseOnBlur = true,
    defaultOpen = false,
    onOpenChange,
    enableAnimation = true,
    mobileBreakpoint = MOBILE_BOTTOM_SHEET_BREAKPOINT
  } = props;

  const deviceWidth = useResize();
  const isMobile = isMobileViewport(deviceWidth, mobileBreakpoint);

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const listBoxRef = useRef<HTMLUListElement>(null);
  const mobileListRef = useRef<HTMLDivElement>(null);
  const prevIsMobileRef = useRef(isMobile);

  useEffect(() => {
    if (!(isOpen && !isMobile)) return;

    const previousInlineOverflow = readBodyInlineOverflow();
    document.body.style.setProperty('overflow', 'hidden');

    return () => {
      writeBodyInlineOverflow(previousInlineOverflow);
    };
  }, [isOpen, isMobile]);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isDisabled) return;
      setIsOpen(open);
      onOpenChange?.(open);
      if (!open) {
        setHighlightedIndex(-1);
        if (shouldCloseOnBlur) {
          onBlur?.();
        }
      }
    },
    [isDisabled, onOpenChange, shouldCloseOnBlur, onBlur]
  );

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange?.(optionValue);
      handleOpenChange(false);
    },
    [onChange, handleOpenChange]
  );

  const positionStyles = useDropdownPosition(
    triggerRef,
    popoverRef,
    isOpen && !isMobile,
    placement,
    offset
  );

  useEffect(() => {
    if (prevIsMobileRef.current !== isMobile && isOpen) {
      handleOpenChange(false);
    }
    prevIsMobileRef.current = isMobile;
  }, [isMobile, isOpen, handleOpenChange]);

  useEffect(() => {
    if (!isOpen || isMobile || !closeOnScroll) return;

    const handleScroll = (event: Event) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (mobileListRef.current?.contains(target)) return;
      if (isNodeInsideBottomSheet(target)) return;
      handleOpenChange(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, isMobile, closeOnScroll, handleOpenChange]);

  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      handleOpenChange(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isMobile, handleOpenChange]);

  useEffect(() => {
    if (!isOpen || !value) return;

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (!isMobile && listBoxRef.current) {
          const selectedItem = listBoxRef.current.querySelector(
            `[role="option"][aria-selected="true"]`
          ) as HTMLElement;
          if (selectedItem) {
            selectedItem.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }

        if (isMobile && mobileListRef.current) {
          const selectedItem = mobileListRef.current.querySelector(
            `[role="option"][aria-selected="true"]`
          ) as HTMLElement;
          if (selectedItem) {
            selectedItem.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }
      }, 50);
    });
  }, [isOpen, value, isMobile, options]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isDisabled) return;

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (isOpen && highlightedIndex >= 0) {
            const option = options[highlightedIndex];
            if (option && !option.disabled) {
              handleSelect(option.value);
            }
          } else {
            handleOpenChange(!isOpen);
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) {
            handleOpenChange(true);
          } else {
            setHighlightedIndex((prev) => {
              const next = prev + 1;
              return next >= options.length ? 0 : next;
            });
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen) {
            handleOpenChange(true);
          } else {
            setHighlightedIndex((prev) => {
              const next = prev - 1;
              return next < 0 ? options.length - 1 : next;
            });
          }
          break;
        case 'Escape':
          event.preventDefault();
          handleOpenChange(false);
          triggerRef.current?.focus();
          break;
        case 'Tab':
          if (isOpen) {
            handleOpenChange(false);
          }
          break;
      }
    },
    [
      isDisabled,
      isOpen,
      highlightedIndex,
      options,
      handleSelect,
      handleOpenChange
    ]
  );

  const hasError = Boolean(error);
  const showError = hasError && !isOpen;
  const showHelperText = (!hasError || isOpen) && helperText;

  const animationClasses = enableAnimation
    ? cn(
      'transition-all duration-200 ease-out',
      isOpen
        ? 'opacity-100 transform scale-100'
        : 'opacity-0 transform scale-95'
    )
    : '';

  const mobileListHeightStyle = getMobileListHeightStyle(120);

  const renderMobileContent = () => (
    <div
      ref={mobileListRef}
      className="overflow-y-auto flex-1 p-2 px-3 flex flex-col gap-2 box-border overscroll-contain touch-pan-y"
      style={mobileListHeightStyle}
    >
      {options.length > 0 ? (
        options.map((option) => (
          <div
            key={option.value}
            onClick={() => {
              if (!option.disabled) {
                handleSelect(option.value);
              }
            }}
            role="option"
            aria-selected={option.value === value}
            aria-disabled={option.disabled}
            className={cn(
              DEFAULT_STYLES.select__bottomSheet__option,
              option.value === value &&
              DEFAULT_STYLES.select__bottomSheet__optionSelected,
              option.disabled &&
              DEFAULT_STYLES.select__bottomSheet__optionDisabled,
              optionClassName
            )}
          >
            <div className="flex items-center justify-between w-full">
              <span className="flex-1 break-words whitespace-normal">
                {option.label}
              </span>
            </div>
          </div>
        ))
      ) : (
        <div className={DEFAULT_STYLES.dropdown__noResults}>
          No options found
        </div>
      )}
    </div>
  );

  const renderDesktopOptions = () => (
    <ul
      ref={listBoxRef}
      role="listbox"
      aria-labelledby={label ? `${name}-label` : undefined}
      className="py-2 px-2 overflow-y-auto outline-none box-border"
      style={{ maxHeight: 'inherit' }}
    >
      {options.map((option, index) => (
        <li
          key={option.value}
          role="option"
          aria-selected={option.value === value}
          aria-disabled={option.disabled}
          onClick={() => {
            if (!option.disabled) {
              handleSelect(option.value);
            }
          }}
          onMouseEnter={() => setHighlightedIndex(index)}
          className={cn(
            DEFAULT_STYLES.dropdown__option,
            option.value === value && DEFAULT_STYLES.dropdown__optionSelected,
            option.disabled && DEFAULT_STYLES.dropdown__optionDisabled,
            highlightedIndex === index &&
            !option.disabled &&
            'bg-bottomSheetOptionBg',
            optionClassName
          )}
        >
          <div className="flex items-center justify-between w-full">
            <span className="flex-1 break-words whitespace-normal">
              {option.label}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className={cn(DEFAULT_STYLES.field, className, 'base__select')}>
      {label && (
        <label
          id={`${name}-label`}
          className={cn(
            DEFAULT_STYLES.field__label,
            isDisabled && DEFAULT_STYLES.field__labelDisabled,
            labelClassName
          )}
        >
          {label}
          {isRequired && (
            <span className={DEFAULT_STYLES.field__labelRequired}>*</span>
          )}
        </label>
      )}

      <input type="hidden" name={name} value={value || ''} />

      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-labelledby={label ? `${name}-label` : undefined}
          aria-disabled={isDisabled}
          disabled={isDisabled}
          onClick={() => handleOpenChange(!isOpen)}
          onKeyDown={handleKeyDown}
          className={cn(
            DEFAULT_STYLES.select__dropdown__trigger,
            showError
              ? 'border-errorRed'
              : isOpen
                ? DEFAULT_STYLES.select__dropdown__triggerOpen
                : DEFAULT_STYLES.field__inputContainerDefault,
            isDisabled && DEFAULT_STYLES.field__inputContainerDisabled,
            'items-start'
          )}
        >
          <span
            className={cn(
              !selectedOption
                ? 'text-placeholderText'
                : DEFAULT_STYLES.field__inputText,
              'break-words whitespace-normal flex-1 text-left'
            )}
          >
            {selectedOption?.label || placeholder}
          </span>
          <svg
            className={cn(
              'w-4 h-4 text-cement flex-shrink-0 ml-2 transform transition-transform duration-300 ease-in-out',
              isOpen ? 'rotate-180' : 'rotate-0'
            )}
            fill="none"
            stroke="#475569"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {isOpen && !isMobile && (
          <Portal>
            <FocusScope contain restoreFocus>
              <div
                ref={popoverRef}
                className={cn(
                  'bg-white border border-lightBlueGray300 rounded-lg shadow-lg overflow-hidden flex flex-col box-border',
                  animationClasses,
                  'base__select__dropdown'
                )}
                style={{
                  ...positionStyles,
                  width: triggerRef.current?.offsetWidth,
                  maxHeight: `${maxHeight}px`,
                  zIndex: 9999
                }}
              >
                <DismissButton onDismiss={() => handleOpenChange(false)} />
                {renderDesktopOptions()}
                <DismissButton onDismiss={() => handleOpenChange(false)} />
              </div>
            </FocusScope>
          </Portal>
        )}

        {isOpen && isMobile && (
          <BottomSheet
            isOpen={isOpen}
            onClose={() => handleOpenChange(false)}
            title={label}
            showCloseButton={true}
            className={DEFAULT_STYLES.select__bottomSheet}
          >
            {renderMobileContent()}
          </BottomSheet>
        )}
      </div>

      {(hasError || showHelperText) && (
        <p
          className={cn(
            DEFAULT_STYLES.field__message,
            showError
              ? 'text-errorTextColor'
              : showHelperText
                ? 'text-helperTextColor'
                : 'invisible h-4'
          )}
          role={showError ? 'alert' : undefined}
        >
          {showError && error ? (
            <FieldErrorMessage message={error} />
          ) : showHelperText ? (
            helperText
          ) : (
            ''
          )}
        </p>
      )}
    </div>
  );
};

Select.displayName = 'Select';
